import axios, { type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import type {
  LoginResult,
  Producer,
  Project,
  Analysis,
  DashboardKPIs,
  AgentConfig,
  CostSummary,
  PaginatedResponse,
  ApiResponse,
  User,
} from '@/types'

// ─── Axios Instance ────────────────────────────────────────────────────────────

const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

// Token storage helpers (avoid direct localStorage mutation in interceptors)
const getToken = (): string | null => localStorage.getItem('agrocredit:token')
const getRefreshToken = (): string | null => localStorage.getItem('agrocredit:refreshToken')
const setTokens = (token: string, refreshToken: string): void => {
  localStorage.setItem('agrocredit:token', token)
  localStorage.setItem('agrocredit:refreshToken', refreshToken)
}
const clearTokens = (): void => {
  localStorage.removeItem('agrocredit:token')
  localStorage.removeItem('agrocredit:refreshToken')
}

// ─── Request Interceptor – attach Bearer token ─────────────────────────────────

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// ─── Response Interceptor – refresh on 401 ────────────────────────────────────

let isRefreshing = false
let pendingRequests: Array<(token: string) => void> = []

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original: AxiosRequestConfig & { _retry?: boolean } = error.config

    if (error.response?.status === 401 && !original._retry) {
      const refreshToken = getRefreshToken()

      if (!refreshToken) {
        clearTokens()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          pendingRequests.push((token: string) => {
            if (original.headers) original.headers.Authorization = `Bearer ${token}`
            resolve(api(original))
          })
        })
      }

      original._retry = true
      isRefreshing = true

      try {
        const { data } = await axios.post<ApiResponse<LoginResult>>('/api/auth/refresh', {
          refreshToken,
        })

        const newToken = data.data!.token
        const newRefreshToken = data.data!.refreshToken
        setTokens(newToken, newRefreshToken)

        pendingRequests.forEach((cb) => cb(newToken))
        pendingRequests = []

        if (original.headers) original.headers.Authorization = `Bearer ${newToken}`
        return api(original)
      } catch {
        clearTokens()
        window.location.href = '/login'
        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)

// ─── Auth ──────────────────────────────────────────────────────────────────────

export const authApi = {
  login: (email: string, password: string, mfaCode?: string) =>
    api.post<ApiResponse<LoginResult>>('/auth/login', { email, password, mfaCode }),

  logout: () => api.post('/auth/logout'),

  refreshToken: (refreshToken: string) =>
    api.post<ApiResponse<LoginResult>>('/auth/refresh', { refreshToken }),

  me: () => api.get<ApiResponse<User>>('/auth/me'),
}

// ─── Producers ────────────────────────────────────────────────────────────────

export const producersApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get<ApiResponse<PaginatedResponse<Producer>>>('/producers', { params }),

  get: (id: string) =>
    api.get<ApiResponse<Producer>>(`/producers/${id}`),

  create: (data: Partial<Producer>) =>
    api.post<ApiResponse<Producer>>('/producers', data),

  update: (id: string, data: Partial<Producer>) =>
    api.patch<ApiResponse<Producer>>(`/producers/${id}`, data),

  delete: (id: string) =>
    api.delete(`/producers/${id}`),

  uploadDocument: (producerId: string, formData: FormData) =>
    api.post<ApiResponse<{ url: string; id: string }>>(
      `/producers/${producerId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    ),
}

// ─── Projects ─────────────────────────────────────────────────────────────────

export const propertiesApi = {
  createForProducer: (producerId: string, data: any) =>
    api.post<ApiResponse<any>>(`/producers/${producerId}/properties`, data),

  getForProducer: (producerId: string) =>
    api.get<ApiResponse<any[]>>(`/producers/${producerId}/properties`),
}

export const projectsApi = {
  list: (params?: { page?: number; limit?: number; status?: string; producerId?: string }) =>
    api.get<ApiResponse<PaginatedResponse<Project>>>('/projects', { params }),

  get: (id: string) =>
    api.get<ApiResponse<Project>>(`/projects/${id}`),

  create: (data: Partial<Project>) =>
    api.post<ApiResponse<Project>>('/projects', data),

  update: (id: string, data: Partial<Project>) =>
    api.patch<ApiResponse<Project>>(`/projects/${id}`, data),

  delete: (id: string) =>
    api.delete(`/projects/${id}`),

  startAnalysis: (id: string) =>
    api.post<ApiResponse<Analysis>>(`/projects/${id}/analyze`),

  getAnalysis: (id: string) =>
    api.get<ApiResponse<Analysis>>(`/projects/${id}/analysis`),

  downloadReport: (id: string, type: 'pdf' | 'xlsx') =>
    api.get(`/projects/${id}/reports/${type}`, { responseType: 'blob' }),
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export const dashboardApi = {
  getKPIs: () =>
    api.get<ApiResponse<DashboardKPIs>>('/dashboard/kpis'),
}

// ─── Admin – Agents ───────────────────────────────────────────────────────────

export const agentsApi = {
  list: () =>
    api.get<ApiResponse<AgentConfig[]>>('/admin/agents'),

  update: (id: string, data: Partial<AgentConfig>) =>
    api.patch<ApiResponse<AgentConfig>>(`/admin/agents/${id}`, data),

  pause: (id: string) =>
    api.post(`/admin/agents/${id}/pause`),

  restart: (id: string) =>
    api.post(`/admin/agents/${id}/restart`),
}

// ─── Admin – Costs ────────────────────────────────────────────────────────────

export const costsApi = {
  getSummary: (month?: string) =>
    api.get<ApiResponse<CostSummary>>('/admin/costs', { params: { month } }),
}

// ─── Admin – Users ────────────────────────────────────────────────────────────

export const usersApi = {
  list: (params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<PaginatedResponse<User>>>('/admin/users', { params }),

  create: (data: Partial<User> & { password: string }) =>
    api.post<ApiResponse<User>>('/admin/users', data),

  update: (id: string, data: Partial<User>) =>
    api.patch<ApiResponse<User>>(`/admin/users/${id}`, data),

  delete: (id: string) =>
    api.delete(`/admin/users/${id}`),
}

export default api
